/**
 * Language and layout direction.
 *
 * This is the single source of truth for which language the UI is in. Two things
 * depend on it and must never drift apart:
 *
 * 1. the i18next instance (interface strings), and
 * 2. every API request, which sends the language so the server can resolve
 *    bilingual content and error messages.
 *
 * Because the server resolves display text per request, the language is also part
 * of every React Query cache key -- otherwise switching to Arabic would re-render
 * cached English titles.
 */

import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type { Language } from '@/types/api'

export const SUPPORTED_LANGUAGES: Language[] = ['en', 'ar']
export const RTL_LANGUAGES: Language[] = ['ar']

export const isRtl = (language: Language): boolean => RTL_LANGUAGES.includes(language)

export const normalizeLanguage = (value: string | null | undefined): Language => {
  if (!value) return 'en'
  const base = value.toLowerCase().replace('_', '-').split('-')[0]
  return (SUPPORTED_LANGUAGES as string[]).includes(base) ? (base as Language) : 'en'
}

interface LanguageState {
  language: Language
  setLanguage: (language: Language) => void
  toggle: () => void
}

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      language: 'en',
      setLanguage: (language) => set({ language: normalizeLanguage(language) }),
      toggle: () => set({ language: get().language === 'ar' ? 'en' : 'ar' }),
    }),
    { name: 'knysa-library-language' },
  ),
)

/** Non-reactive accessor for the axios interceptor. */
export const currentLanguage = (): Language => useLanguageStore.getState().language

export const direction = (language: Language): 'rtl' | 'ltr' => (isRtl(language) ? 'rtl' : 'ltr')
